"use client";

import { ChevronRightIcon } from "lucide-react";
import type { ReactNode } from "react";

import { AssistantHeaderTrigger } from "@/components/assistant/assistant-panel";
import { LoadingPage } from "@/components/loading-page";
import { Skeleton } from "@/components/ui/skeleton";
import { pageTitleClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

import { SidebarTrigger } from "./ui/sidebar";

interface PageWrapperProps {
  title: string;
  children: ReactNode;
  /**
   * Optional back control rendered in the title row (after the sidebar trigger,
   * before the page title). Use for "leave this screen" navigation — not for CTAs.
   */
  back?: ReactNode;
  actions?: ReactNode;
  /**
   * Ancestor trail rendered on the **same line** as the title, before it
   * (e.g. `Projetos` › `PROJ-0003`). Prefer `PageBreadcrumbs` for parent segments.
   */
  breadcrumb?: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "wizard" | "full";
  className?: string;
}

const MAX_WIDTH_CLASSES: Record<NonNullable<PageWrapperProps["maxWidth"]>, string> = {
  sm: "max-w-3xl",
  md: "max-w-5xl",
  lg: "max-w-6xl",
  xl: "max-w-7xl",
  "2xl": "max-w-[1360px]",
  wizard: "max-w-[1400px]",
  full: "max-w-none",
};

export function PageWrapper({
  children,
  title,
  back,
  actions,
  breadcrumb,
  maxWidth = "2xl",
  className,
}: PageWrapperProps) {
  return (
    <PageFrame
      actions={actions}
      body={
        <div
          className={cn(
            "mx-auto w-full p-4 text-xs/relaxed sm:px-6 sm:pt-6 lg:px-8",
            MAX_WIDTH_CLASSES[maxWidth],
            className,
          )}
        >
          {children}
        </div>
      }
    >
      {back ? (
        <div className="shrink-0" data-testid="page-back">
          {back}
        </div>
      ) : null}
      {breadcrumb ? (
        <nav
          aria-label="Breadcrumb"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-sm sm:text-base"
          data-testid="page-title-trail"
        >
          {/* Phones keep the current title; header actions leave no room for ancestors. */}
          <div className="hidden min-w-0 shrink items-center sm:flex">{breadcrumb}</div>
          <ChevronRightIcon
            aria-hidden="true"
            className="text-muted-foreground hidden size-3.5 shrink-0 sm:block"
          />
          <h1 aria-current="page" className={cn(pageTitleClassName, "min-w-0")}>
            {title}
          </h1>
        </nav>
      ) : (
        <div className="min-w-0 flex-1" data-testid="page-title-trail">
          <h1 className={pageTitleClassName}>{title}</h1>
        </div>
      )}
    </PageFrame>
  );
}

/**
 * Route-level loading state for app pages. Keeps the header row in place so the
 * page does not jump when the real `PageWrapper` resolves.
 */
export function PageWrapperLoading() {
  return (
    <PageFrame body={<LoadingPage />}>
      <Skeleton className="h-5 w-32" />
    </PageFrame>
  );
}

/** Shared header row + scroll area. `children` is the title area after the sidebar trigger. */
function PageFrame({
  children,
  actions,
  body,
}: {
  children: ReactNode;
  actions?: ReactNode;
  body: ReactNode;
}) {
  return (
    <div className="bg-background flex min-h-0 flex-1 flex-col">
      <header className="bg-sidebar shrink-0 border-b p-4 lg:px-6" data-testid="page-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <SidebarTrigger className="shrink-0" />
            {children}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {actions && (
              <div
                className="flex shrink-0 flex-wrap items-center justify-end gap-2"
                data-testid="page-actions"
              >
                {actions}
              </div>
            )}
            <AssistantHeaderTrigger />
          </div>
        </div>
      </header>

      {/* The sidebar inset is the page <main>; this is only the scroll area. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">{body}</div>
    </div>
  );
}
